/**
 * ==============================================================================
 * LAROYE HATS & THEJOKERKCCAPS — SHOPIFY STOREFRONT API CLIENT
 * ==============================================================================
 * Powers the custom headless front end with live Shopify products, inventory,
 * and direct checkout routing into Daniel and his wife's Shopify backend.
 * ==============================================================================
 */

class ShopifyStorefrontClient {
  constructor(config = window.SHOPIFY_CONFIG) {
    this.config = config || {};
    this.shopDomain = this.config.shopDomain || 'laroye-hats.myshopify.com';
    this.accessToken = this.config.storefrontAccessToken || '';
    this.apiVersion = this.config.apiVersion || '2024-07';
    this.endpoint = `https://${this.shopDomain}/api/${this.apiVersion}/graphql.json`;
    this.isConnected = false;
  }

  /**
   * Execute a GraphQL query against Shopify Storefront API
   */
  async graphql(query, variables = {}) {
    if (!this.accessToken) {
      console.info('[Shopify Storefront] No Access Token provided. Running in high-fidelity demo catalog mode.');
      return null;
    }

    try {
      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.accessToken
        },
        body: JSON.stringify({ query, variables })
      });

      if (!response.ok) {
        throw new Error(`Shopify API responded with status ${response.status}`);
      }

      const json = await response.json();
      if (json.errors) {
        console.error('[Shopify GraphQL Error]', json.errors);
        throw new Error(json.errors[0]?.message || 'GraphQL query failed');
      }

      this.isConnected = true;
      return json.data;
    } catch (err) {
      console.warn('[Shopify Client Error]', err.message);
      return null;
    }
  }

  /**
   * Test connection to Daniel's Shopify store
   */
  async testConnection() {
    if (!this.accessToken) {
      return { connected: false, message: 'Storefront Access Token not configured yet (Demo Mode)' };
    }

    const query = `
      query {
        shop {
          name
          primaryDomain {
            url
            host
          }
        }
      }
    `;

    const data = await this.graphql(query);
    if (data && data.shop) {
      this.isConnected = true;
      return { connected: true, shopName: data.shop.name, host: data.shop.primaryDomain?.host };
    }
    return { connected: false, message: 'Unable to connect to Shopify store. Check domain & token.' };
  }

  /**
   * Fetch Live Products from Shopify with automatic pagination and map them to our template format
   */
  async fetchProducts(maxProducts = 1000, forceRefresh = false) {
    const CACHE_KEY = 'laroye_products_cache';
    const CACHE_TTL = 10 * 60 * 1000; // 10 minutes

    if (!forceRefresh) {
      try {
        const cached = sessionStorage.getItem(CACHE_KEY);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (parsed && parsed.timestamp && (Date.now() - parsed.timestamp < CACHE_TTL) && Array.isArray(parsed.products) && parsed.products.length > 0) {
            console.log(`[Shopify Storefront] Loaded ${parsed.products.length} products from session cache.`);
            return parsed.products;
          }
        }
      } catch (e) {
        console.warn('[Shopify Client] Cache read error:', e);
      }
    }

    const query = `
      query getProducts($first: Int!, $after: String) {
        products(first: $first, after: $after, sortKey: CREATED_AT, reverse: true) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              title
              description
              handle
              vendor
              productType
              tags
              createdAt
              availableForSale
              images(first: 5) {
                edges {
                  node {
                    url
                    altText
                  }
                }
              }
              variants(first: 20) {
                edges {
                  node {
                    id
                    title
                    price {
                      amount
                      currencyCode
                    }
                    compareAtPrice {
                      amount
                      currencyCode
                    }
                    availableForSale
                  }
                }
              }
            }
          }
        }
      }
    `;

    let allProductNodes = [];
    let cursor = null;
    let hasNextPage = true;

    try {
      while (hasNextPage && allProductNodes.length < maxProducts) {
        const pageSize = Math.min(100, maxProducts - allProductNodes.length);
        const data = await this.graphql(query, { first: pageSize, after: cursor });
        if (!data || !data.products || !data.products.edges || !data.products.edges.length) break;

        allProductNodes.push(...data.products.edges.map(e => e.node));
        hasNextPage = Boolean(data.products.pageInfo?.hasNextPage);
        cursor = data.products.pageInfo?.endCursor || null;
        if (!cursor) break;
      }
    } catch (err) {
      console.warn('[Shopify Client] Pagination error:', err.message);
    }

    if (!allProductNodes.length) {
      return null; // Fall back to design catalog
    }

    // Map Shopify GraphQL products to template schema
    const mappedProducts = allProductNodes.map((node, index) => {
      const firstVariant = node.variants?.edges?.[0]?.node;
      const price = firstVariant ? parseFloat(firstVariant.price.amount) : 68.00;
      const oldPrice = firstVariant?.compareAtPrice ? parseFloat(firstVariant.compareAtPrice.amount) : null;
      
      const images = node.images?.edges?.map(e => e.node.url) || [];
      const img = images[0] || 'assets/img/laroye_hat_black_red.jpg';

      const variants = (node.variants?.edges || []).map(e => ({
        id: e.node.id,
        title: (!e.node.title || e.node.title.trim() === '' || e.node.title === 'Default Title') ? 'One Size Adjustable' : e.node.title.trim(),
        price: parseFloat(e.node.price?.amount || price),
        compareAtPrice: e.node.compareAtPrice ? parseFloat(e.node.compareAtPrice.amount) : null,
        availableForSale: Boolean(e.node.availableForSale)
      }));

      const sizes = variants.map(v => v.title);

      const rawTags = Array.isArray(node.tags) ? node.tags : [];
      const cleanTags = rawTags.map(t => (t || '').trim()).filter(Boolean);
      const tagsList = cleanTags.map(t => t.toLowerCase());

      // Check for pre-sale tag or title
      const isPresale = tagsList.some(t => t.includes('pre')) || 
                        node.title.toLowerCase().includes('pre-sale') ||
                        node.title.toLowerCase().includes('preventa');

      let badge = '';
      if (!node.availableForSale) badge = 'Sold Out';
      else if (isPresale) badge = 'Pre-Sale';
      else if (oldPrice && oldPrice > price) badge = 'Sale';
      else if (tagsList.includes('signature')) badge = 'Signature';
      else if (tagsList.includes('limited')) badge = 'Limited Drop';

      // Detect Brand from Shopify vendor, tags, or title
      let brand = 'Laroye Hats';
      const titleLower = node.title.toLowerCase();
      const vendorLower = (node.vendor || '').toLowerCase();

      for (const t of tagsList) {
        if (t.includes('bigg boss')) { brand = 'BIGG BOSS'; break; }
        if (t.includes('sad boyz')) { brand = 'SAD BOYZ'; break; }
        if (t.includes('gallo fino') || t === 'gallos') { brand = 'GALLO FINO'; break; }
        if (t.includes('markitos')) { brand = 'MARKITOS TOYS'; break; }
        if (t.includes('exodo')) { brand = 'ÉXODO'; break; }
        if (t.includes('modelo ct') || t === 'ct') { brand = 'CORRIDOS TUMBADOS'; break; }
        if (t.includes('chava')) { brand = 'CHAVA HATS'; break; }
        if (t.includes('rico o muerto')) { brand = 'RICO O MUERTO'; break; }
        if (t.includes('77 modelos') || t === '77') { brand = '77 MODELOS'; break; }
        if (t.includes('pantera')) { brand = 'PANTERA'; break; }
      }

      if (brand === 'Laroye Hats') {
        if (titleLower.includes('by bb') || titleLower.startsWith('bb ') || titleLower.includes('b star') || titleLower.includes('bigg boss') || vendorLower.includes('bigg boss')) {
          brand = 'BIGG BOSS';
        } else if (titleLower.includes('caps fans') || vendorLower.includes('caps fans')) {
          brand = 'CAPS FANS';
        } else if (titleLower.includes('sad boyz') || titleLower.includes('sad boss')) {
          brand = 'SAD BOYZ';
        } else if (titleLower.includes('gallo fino') || titleLower.includes('tumbado fino')) {
          brand = 'GALLO FINO';
        } else if (titleLower.includes('markitos toys')) {
          brand = 'MARKITOS TOYS';
        }
      }

      // Detect City / Style from tags or title
      let style = 'Custom Editions';

      for (const t of tagsList) {
        if (
          t === 'la' || 
          t === 'l.a' || 
          t === 'l.a.' || 
          t.includes('l.a') || 
          t.includes('los angeles') || 
          t.includes('los ángeles') || 
          t.includes('la models') || 
          t.includes('dodgers')
        ) {
          style = 'Los Angeles';
          break;
        }
        if (t === 'ny' || t === 'n.y' || t.includes('new york') || t.includes('yankees') || t.includes('mets')) {
          style = 'New York';
          break;
        }
        if (t === 'sd' || t === 's.d' || t.includes('san diego') || t.includes('padres')) {
          style = 'San Diego';
          break;
        }
        if (t === 'chi' || t.includes('chicago') || t.includes('white sox') || t.includes('bulls')) {
          style = 'Chicago';
          break;
        }
        if (t.includes('houston') || t.includes('texas') || t.includes('astros') || t.includes('dallas')) {
          style = 'Texas';
          break;
        }
        if (t.includes('boston') || t.includes('red sox')) {
          style = 'Boston';
          break;
        }
        if (t.includes('detroit') || t.includes('tigers')) {
          style = 'Detroit';
          break;
        }
        if (t.includes('mexico') || t.includes('méxico')) {
          style = 'Mexico';
          break;
        }
        if (t === 'equipos' || t.includes('equipo')) {
          style = 'Equipos';
          break;
        }
      }

      if (style === 'Custom Editions') {
        if (
          titleLower.includes('los angeles') || 
          titleLower.includes('los ángeles') || 
          titleLower.includes('los angles') || 
          titleLower.includes('dodgers') ||
          (/\b(LA|L\.A)\b/.test(node.title) && !/\bLa\s+(Guadalupana|Santa|Muerte|Virgen|Banda)\b/i.test(node.title))
        ) {
          style = 'Los Angeles';
        } else if (
          titleLower.includes('new york') || 
          titleLower.includes('yankees') || 
          titleLower.includes('mets') ||
          /\b(NY|N\.Y)\b/.test(node.title)
        ) {
          style = 'New York';
        } else if (
          titleLower.includes('san diego') || 
          titleLower.includes('padres') ||
          /\b(SD|S\.D)\b/.test(node.title)
        ) {
          style = 'San Diego';
        } else if (
          titleLower.includes('chicago') || 
          titleLower.includes('white sox') || 
          titleLower.includes('bulls') ||
          /\bCHI\b/.test(node.title)
        ) {
          style = 'Chicago';
        } else if (
          titleLower.includes('houston') || 
          titleLower.includes('astros') || 
          titleLower.includes('texas') || 
          titleLower.includes('dallas')
        ) {
          style = 'Texas';
        } else if (titleLower.includes('boston') || titleLower.includes('red sox')) {
          style = 'Boston';
        } else if (titleLower.includes('detroit') || titleLower.includes('tigers')) {
          style = 'Detroit';
        } else if (titleLower.includes('mexico') || titleLower.includes('méxico')) {
          style = 'Mexico';
        }
      }

      // Detect Hat Type / Series from Daniel's live Shopify tags and titles
      let hatType = 'Special Editions';
      for (const t of tagsList) {
        if (t.includes('cruce')) { hatType = 'Cruces Series'; break; }
        if (t.includes('cristo') || t.includes('jesus') || t.includes('jesús') || t.includes('judas') || t.includes('virgen') || t.includes('orisha') || t.includes('santo') || t.includes('rosario')) {
          hatType = 'Religiosas Series';
          break;
        }
        if (t.includes('gallo')) { hatType = 'Gallos Series'; break; }
        if (t.includes('modelo ct') || t === 'ct') { hatType = 'Tumbados Series'; break; }
        if (t.includes('equipo')) { hatType = 'Equipos Series'; break; }
        if (t.includes('cristal') || t.includes('crystal')) { hatType = 'Cristales Series'; break; }
        if (t.includes('anniversary') || t.includes('aniversario')) { hatType = 'Anniversary Series'; break; }
      }

      if (hatType === 'Special Editions') {
        if (titleLower.includes('cruce') || titleLower.includes('cruces')) {
          hatType = 'Cruces Series';
        } else if (titleLower.includes('cristal') || titleLower.includes('cristales') || titleLower.includes('crystal')) {
          hatType = 'Cristales Series';
        } else if (titleLower.includes('anniversary') || titleLower.includes('aniversario')) {
          hatType = 'Anniversary Series';
        } else if (titleLower.includes('eternity')) {
          hatType = 'Eternity Series';
        } else if (titleLower.includes('guadalupana') || titleLower.includes('san judas') || titleLower.includes('virgen') || titleLower.includes('cristo') || titleLower.includes('jesús') || titleLower.includes('jesus') || titleLower.includes('orisha') || titleLower.includes('santo') || titleLower.includes('rosario') || titleLower.includes('bendecido')) {
          hatType = 'Religiosas Series';
        } else if (titleLower.includes('gallo')) {
          hatType = 'Gallos Series';
        } else if (titleLower.includes('tumbado')) {
          hatType = 'Tumbados Series';
        } else if (node.productType && node.productType.trim() !== '') {
          hatType = node.productType.trim();
        }
      }

      return {
        id: node.id,
        variantId: firstVariant?.id || `var_${index}`,
        name: node.title,
        brand: brand,
        style: style,
        city: style,
        hatType: hatType,
        collection: style,
        price: price,
        oldPrice: oldPrice,
        badge: badge,
        inStock: node.availableForSale,
        isPresale: isPresale,
        scarcityText: isPresale ? '⚡ Pre-Sale Drop (Final Sale)' : '⚡ Limited Stock',
        variants: variants,
        sizes: sizes.length ? sizes : ['One Size Adjustable'],
        img: img,
        images: images.length ? images : [img],
        tags: cleanTags,
        createdAt: node.createdAt || null,
        description: (node.description && node.description.trim().length > 0) 
          ? node.description.trim() 
          : this.generateRichDescription(node.title, brand, hatType, isPresale)
      };
    });

    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({
        timestamp: Date.now(),
        products: mappedProducts
      }));
    } catch (e) {
      console.warn('[Shopify Client] Could not cache to sessionStorage:', e);
    }

    return mappedProducts;
  }

  generateRichDescription(title, brand, hatType, isPresale) {
    const bName = brand || 'Laroye Hats';
    const tLower = (title || '').toLowerCase();
    const typeLower = (hatType || '').toLowerCase();

    let desc = '';
    if (typeLower.includes('cruces') || tLower.includes('cruces') || tLower.includes('cruce')) {
      desc = `Signature ${bName} Cruces Series release. Crafted from heavyweight structured twill featuring intricate cross embroidery with ultra-dense stitch counts, signature underbrim detailing, custom interior taping, and a tailored crown profile.`;
    } else if (typeLower.includes('cristales') || tLower.includes('cristal') || tLower.includes('crystal')) {
      desc = `Ultra-luxe ${bName} Cristales Series custom piece. Embellished with precision hand-set micro-crystals that catch light from every angle, rich contrast metallic stitching, and a structured athletic crown.`;
    } else if (typeLower.includes('anniversary') || tLower.includes('anniversary')) {
      desc = `Commemorative ${bName} Anniversary Vault Edition. Engineered with gold and silver metallic thread embroidery, custom anniversary side crest, satin inner sweatband, and collector-grade craftsmanship.`;
    } else if (typeLower.includes('eternity') || tLower.includes('eternity')) {
      desc = `Signature ${bName} Eternity Series custom cap. Unites timeless luxury streetwear aesthetics with structured 6-panel architecture, moisture-wicking inner headband, and high-relief dimensional stitchwork.`;
    } else if (typeLower.includes('religiosas') || tLower.includes('san judas') || tLower.includes('guadalupana') || tLower.includes('bendecido')) {
      desc = `Heritage Religiosas Series custom cap by ${bName}. Features intricate raised 3D threadwork of sacred iconography, glow-in-the-dark luminous accents, and a reinforced structured visor designed for lasting honor.`;
    } else if (tLower.includes('murakami')) {
      desc = `Exclusive ${bName} Art Edition cap featuring custom floral motif embroidery, radiant crystal embellishments, and a structured luxury silhouette.`;
    } else if (tLower.includes('chrome')) {
      desc = `Metallic Chrome series custom cap by ${bName}. Features liquid silver reflective highlights, deep monochrome twill, and a sculpted streetwear profile.`;
    } else {
      desc = `Authentic custom headwear piece by ${bName}. Handcrafted structured silhouette with ultra-dense dimensional embroidery, reinforced crown panels, and premium inner lining. Born to be different.`;
    }

    if (isPresale) {
      desc += ' Note: Pre-sale item — custom-manufactured to order. Strictly no cancellations, returns, or refunds.';
    }
    return desc;
  }

  /**
   * Create a Shopify Checkout session for the current cart
   * and redirect customer to Daniel's secure Shopify checkout
   */
  async createCheckout(cartItems, discountCode = '') {
    if (!cartItems || !cartItems.length) return;

    const hasPresale = cartItems.some(i => i.isPresale);

    // If Storefront token is active, use modern cartCreate GraphQL mutation
    if (this.accessToken) {
      const lines = cartItems.map(item => ({
        merchandiseId: item.variantId,
        quantity: item.quantity,
        attributes: item.isPresale ? [
          { key: 'PreSalePolicy', value: 'FINAL SALE: Strictly no cancellations or returns.' }
        ] : []
      }));

      const cartCreateMutation = `
        mutation cartCreate($input: CartInput!) {
          cartCreate(input: $input) {
            cart {
              id
              checkoutUrl
            }
            userErrors {
              field
              message
            }
          }
        }
      `;

      const input = {
        lines: lines,
        discountCodes: discountCode ? [discountCode] : [],
        attributes: hasPresale ? [
          { key: 'OrderNote', value: 'Includes Pre-Sale Item(s). Final Sale: No cancellations or returns.' }
        ] : []
      };

      const data = await this.graphql(cartCreateMutation, { input });
      if (data?.cartCreate?.cart?.checkoutUrl) {
        window.location.href = data.cartCreate.cart.checkoutUrl;
        return;
      }
    }

    // Fallback: Shopify Cart Direct Permalink (Works with or without Storefront API!)
    // Format: https://{shop}.myshopify.com/cart/{variant_id}:{quantity},{variant_id}:{quantity}
    const permalinkItems = cartItems
      .filter(i => i.variantId && !i.variantId.startsWith('laroye_'))
      .map(i => {
        // Extract numeric ID if it's a global GraphQL ID e.g. gid://shopify/ProductVariant/123456
        const id = i.variantId.includes('/') ? i.variantId.split('/').pop() : i.variantId;
        return `${id}:${i.quantity}`;
      });

    if (permalinkItems.length > 0 && this.shopDomain) {
      let checkoutUrl = `https://${this.shopDomain}/cart/${permalinkItems.join(',')}`;
      const params = [];
      if (hasPresale) {
        params.push(`note=${encodeURIComponent('Includes Pre-Sale Item(s) — Final Sale (No Cancellations or Returns)')}`);
      }
      if (discountCode) {
        params.push(`discount=${encodeURIComponent(discountCode)}`);
      }
      if (params.length > 0) {
        checkoutUrl += `?${params.join('&')}`;
      }
      window.location.href = checkoutUrl;
      return;
    }

    // Demo Mode Simulation
    alert(
      hasPresale
        ? "🔒 [SHOPIFY CHECKOUT SIMULATION]\n\nYour order contains custom Pre-Sale item(s).\nPolicy: Strictly FINAL SALE with NO CANCELLATIONS OR RETURNS.\n\nOnce Daniel and his wife add their Storefront API token, this button routes buyers seamlessly into their live Shopify Shop Pay / Apple Pay checkout!"
        : "🔒 [SHOPIFY CHECKOUT SIMULATION]\n\nProceeding to Shopify hosted checkout.\n\nOnce Daniel adds his Storefront API token, this button routes buyers seamlessly into their live Shopify Shop Pay / Apple Pay checkout!"
    );
  }

  /**
   * Save Shopify credentials to localStorage and update client
   */
  saveCredentials(domain, token) {
    if (domain) this.shopDomain = domain.trim().replace(/^https?:\/\//, '').replace(/\/$/, '');
    if (token !== undefined) this.accessToken = token.trim();
    this.endpoint = `https://${this.shopDomain}/api/${this.apiVersion}/graphql.json`;

    try {
      localStorage.setItem('laroye_shopify_config', JSON.stringify({
        shopDomain: this.shopDomain,
        storefrontAccessToken: this.accessToken
      }));
    } catch (e) {}
  }
}

// Global client instance
if (typeof window !== 'undefined') {
  window.ShopifyClient = new ShopifyStorefrontClient();
}

/**
 * Interactive In-Browser Shopify Connect Assistant
 */
function initShopifyConnectUI() {
  if (document.getElementById('shopifyConnectTrigger')) return;

  // Floating trigger button in bottom-left corner
  const btn = document.createElement('button');
  btn.id = 'shopifyConnectTrigger';
  btn.setAttribute('aria-label', 'Connect Shopify Store');
  btn.innerHTML = `
    <span class="sc-dot"></span>
    <span class="sc-label">Shopify: ${window.ShopifyClient.accessToken ? '🟢 Live' : '⚙️ Connect'}</span>
  `;
  btn.style.cssText = `
    position: fixed;
    bottom: 24px;
    left: 24px;
    z-index: 9998;
    background: rgba(14, 14, 16, 0.88);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    border: 1px solid rgba(255, 255, 255, 0.15);
    color: #fff;
    padding: 8px 16px;
    border-radius: 999px;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.5px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    box-shadow: 0 8px 24px rgba(0,0,0,0.5);
    transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
  `;

  btn.addEventListener('mouseenter', () => {
    btn.style.borderColor = 'rgba(229, 9, 20, 0.6)';
    btn.style.transform = 'translateY(-2px)';
  });
  btn.addEventListener('mouseleave', () => {
    btn.style.borderColor = 'rgba(255, 255, 255, 0.15)';
    btn.style.transform = 'translateY(0)';
  });
  btn.addEventListener('click', () => openShopifyConnectModal());

  document.body.appendChild(btn);
}

function openShopifyConnectModal() {
  let modal = document.getElementById('shopifyConnectModal');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'shopifyConnectModal';
    modal.style.cssText = `
      position: fixed;
      inset: 0;
      z-index: 99999;
      background: rgba(0,0,0,0.85);
      backdrop-filter: blur(20px);
      -webkit-backdrop-filter: blur(20px);
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
    `;

    modal.innerHTML = `
      <div style="background: #0E0E10; border: 1px solid rgba(255,255,255,0.12); border-radius: 20px; max-width: 520px; width: 100%; padding: 32px; box-shadow: 0 25px 60px rgba(0,0,0,0.8); color: #fff; font-family: -apple-system, BlinkMacSystemFont, sans-serif; position: relative;">
        <button id="closeShopifyModal" style="position: absolute; top: 20px; right: 20px; background: transparent; border: none; color: #888; font-size: 24px; cursor: pointer; line-height: 1;">&times;</button>
        
        <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 8px;">
          <span style="font-size: 28px;">🛍️</span>
          <div>
            <h2 style="font-size: 20px; font-weight: 800; margin: 0; letter-spacing: -0.5px;">Shopify Storefront Connect</h2>
            <p style="font-size: 13px; color: #888; margin: 2px 0 0;">Connect Daniel & his wife's Shopify backend</p>
          </div>
        </div>

        <div style="background: rgba(229, 9, 20, 0.08); border: 1px solid rgba(229, 9, 20, 0.2); border-radius: 12px; padding: 12px 14px; margin: 18px 0; font-size: 12px; color: #eee; line-height: 1.5;">
          ⚡ <strong>Headless Architecture:</strong> Products sync via Storefront API and checkouts automatically open in Shopify with Shop Pay & Apple Pay.
        </div>

        <div style="margin-bottom: 16px;">
          <label style="display: block; font-size: 12px; font-weight: 700; color: #bbb; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.5px;">Shopify Store Domain</label>
          <input type="text" id="scInputDomain" placeholder="e.g. thejokerkccaps.myshopify.com" value="${window.ShopifyClient.shopDomain || ''}" style="width: 100%; padding: 12px 16px; background: #16161a; border: 1px solid rgba(255,255,255,0.15); border-radius: 10px; color: #fff; font-size: 14px; box-sizing: border-box; outline: none;">
        </div>

        <div style="margin-bottom: 20px;">
          <label style="display: block; font-size: 12px; font-weight: 700; color: #bbb; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.5px;">Storefront API Access Token</label>
          <input type="text" id="scInputToken" placeholder="Paste Storefront Access Token here" value="${window.ShopifyClient.accessToken || ''}" style="width: 100%; padding: 12px 16px; background: #16161a; border: 1px solid rgba(255,255,255,0.15); border-radius: 10px; color: #fff; font-size: 14px; box-sizing: border-box; outline: none;">
          <p style="font-size: 11px; color: #666; margin: 6px 0 0;">From Shopify Admin &gt; Settings &gt; Apps &gt; Develop apps &gt; Storefront API.</p>
        </div>

        <div id="scStatusBox" style="display: none; padding: 12px; border-radius: 10px; font-size: 13px; margin-bottom: 18px;"></div>

        <div style="display: flex; gap: 10px;">
          <button id="scTestBtn" style="flex: 1; padding: 12px; background: #222; border: 1px solid rgba(255,255,255,0.15); color: #fff; border-radius: 10px; font-weight: 700; cursor: pointer; font-size: 13px;">Test Connection</button>
          <button id="scSaveBtn" style="flex: 1; padding: 12px; background: #E50914; border: none; color: #fff; border-radius: 10px; font-weight: 800; cursor: pointer; font-size: 13px; letter-spacing: 0.5px;">Save &amp; Sync Store</button>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    modal.querySelector('#closeShopifyModal').onclick = () => { modal.style.display = 'none'; };
    modal.onclick = (e) => { if (e.target === modal) modal.style.display = 'none'; };

    const statusBox = modal.querySelector('#scStatusBox');

    modal.querySelector('#scTestBtn').onclick = async () => {
      const domain = modal.querySelector('#scInputDomain').value.trim();
      const token = modal.querySelector('#scInputToken').value.trim();
      statusBox.style.display = 'block';
      statusBox.style.background = 'rgba(255,255,255,0.05)';
      statusBox.style.color = '#ccc';
      statusBox.textContent = 'Testing connection to ' + domain + '...';

      window.ShopifyClient.shopDomain = domain;
      window.ShopifyClient.accessToken = token;
      window.ShopifyClient.endpoint = `https://${domain}/api/${window.ShopifyClient.apiVersion}/graphql.json`;

      const res = await window.ShopifyClient.testConnection();
      if (res.connected) {
        statusBox.style.background = 'rgba(46, 213, 115, 0.15)';
        statusBox.style.border = '1px solid #2ed573';
        statusBox.style.color = '#2ed573';
        statusBox.textContent = `✓ Successfully verified! Connected to: "${res.shopName}" (${res.host})`;
      } else {
        statusBox.style.background = 'rgba(255, 71, 87, 0.15)';
        statusBox.style.border = '1px solid #ff4757';
        statusBox.style.color = '#ff4757';
        statusBox.textContent = `✕ ${res.message}`;
      }
    };

    modal.querySelector('#scSaveBtn').onclick = async () => {
      const domain = modal.querySelector('#scInputDomain').value.trim();
      const token = modal.querySelector('#scInputToken').value.trim();
      window.ShopifyClient.saveCredentials(domain, token);
      
      const trigger = document.getElementById('shopifyConnectTrigger');
      if (trigger) {
        trigger.innerHTML = `<span class="sc-dot"></span><span class="sc-label">Shopify: ${token ? '🟢 Live' : '⚙️ Connect'}</span>`;
      }

      statusBox.style.display = 'block';
      statusBox.style.background = 'rgba(46, 213, 115, 0.15)';
      statusBox.style.border = '1px solid #2ed573';
      statusBox.style.color = '#2ed573';
      statusBox.textContent = '✓ Saved! Syncing live catalog...';

      setTimeout(async () => {
        if (typeof loadCatalog === 'function') {
          await loadCatalog();
        }
        modal.style.display = 'none';
      }, 1000);
    };
  }

  modal.style.display = 'flex';
}

